/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { styled, alpha, IconButton, IconButtonProps } from "@mui/material";
import { useClickHandler } from "hooks/ClickHandleHook";

type IconButtonShape = "circle" | "square";

interface BorderedIconButtonProps extends IconButtonProps {
  shape?: IconButtonShape;
}

export const BorderedIconButton: React.FC<BorderedIconButtonProps> = ({
  children,
  shape = "circle",
  onClick,
  ...others
}) => {
  const onClickHandler = useClickHandler(onClick);
  return (
    <StyledButton shape={shape} onClick={onClickHandler} {...others}>
      {children}
    </StyledButton>
  );
};

interface StyledButtonProps {
  shape: IconButtonShape;
}

const StyledButton = styled(IconButton)<StyledButtonProps>(
  ({ theme, shape }) => ({
    padding: theme.spacing(0.25),
    backgroundColor: theme.palette.background.paper,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.grey[500],
    borderRadius: shape === "square" ? "8px" : "50%",
    color: theme.palette.text.primary,
    fontWeight: "normal",
    boxShadow: theme.shadows[3],
    ".MuiSvgIcon-root": {
      width: 27,
      height: 27,
      fill: "currentColor",
    },
    "&:focus": {
      borderColor: theme.palette.grey[500],
    },
    "&:active": {
      boxShadow: "none",
    },
    "&:disabled": {
      backgroundColor: alpha(theme.palette.background.paper, 0.2),
      borderColor: theme.palette.divider,
      boxShadow: "none",
    },
  })
);
